import type { ReactNode } from 'react'

import type { FirewallFlow } from '../api/types'
import { DataTable } from './DataTable'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Il significato, per l'operatore, del **motivo di fine sessione** di Palo Alto
 * (`session_end_reason`) e dell'**azione** di FortiOS (`action` del log di traffico). Funzioni e non stringhe: `t()` si chiama in fase di render.
 */
const STATO_SPIEGA: Record<string, () => string> = {
  'tcp-fin': () => t('Chiusura regolare TCP: entrambe le parti hanno terminato con un FIN.'),
  'tcp-rst-from-client': () => t('Il client ha inviato un reset (RST): sessione chiusa dal suo lato.'),
  'tcp-rst-from-server': () => t('Il server ha inviato un reset (RST): sessione chiusa dal suo lato.'),
  'tcp-reuse': () => t('La stessa coppia porta/indirizzi è stata riusata per una nuova sessione.'),
  'aged-out': () => t('Sessione scaduta per inattività (timeout), non chiusa esplicitamente.'),
  'policy-deny': () => t('Bloccata da una regola di sicurezza.'),
  'threat': () => t('Chiusa perché è stata rilevata una minaccia.'),
  'decrypt-cert-validation': () => t('Chiusa in fase di decrypt SSL: validazione del certificato non riuscita.'),
  'decrypt-error': () => t('Chiusa per un errore durante il decrypt SSL.'),
  'resources-unavailable': () => t('Chiusa per esaurimento di risorse sul firewall.'),
  'unknown': () => t('Sconosciuto: spesso una sessione ancora attiva o non TCP.'),
  'n/a': () => t('Non applicabile: sessione ancora aperta o protocollo senza stato (UDP/ICMP).'),
  // FortiOS
  'close': () => t('Chiusura regolare: la sessione è terminata normalmente.'),
  'timeout': () => t('Sessione scaduta per inattività (timeout), non chiusa esplicitamente.'),
  'client-rst': () => t('Il client ha inviato un reset (RST): sessione chiusa dal suo lato.'),
  'server-rst': () => t('Il server ha inviato un reset (RST): sessione chiusa dal suo lato.'),
  'deny': () => t('Bloccata da una regola di sicurezza.'),
  'accept': () => t('Permessa dalla policy; la sessione era ancora aperta al momento del log.'),
  'ip-conn': () => t('Bloccata per il limite di connessioni per indirizzo IP.'),
}

function spiegaStato(reason?: string | null): string {
  const r = (reason ?? '').toLowerCase()
  return STATO_SPIEGA[r] ? STATO_SPIEGA[r]() : ''
}

function Voce({ k, v }: { k: string; v: ReactNode }) {
  if (v == null || v === '') return null
  return (
    <div>
      <dt style={{ color: 'var(--text-muted)', fontSize: 11 }}>{k}</dt>
      <dd style={{ margin: 0 }}>{v}</dd>
    </div>
  )
}

function Dettaglio({ f }: { f: FirewallFlow }) {
  const spiega = spiegaStato(f.end_reason)
  return (
    <div style={{ padding: '4px 2px' }}>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 10,
          margin: 0,
        }}
      >
        <Voce k={t('Stato sessione')} v={f.end_reason} />
        <Voce k={t('Zona')} v={f.from_zone || f.to_zone ? `${f.from_zone ?? '—'} → ${f.to_zone ?? '—'}` : null} />
        <Voce k={t('Protocollo')} v={f.proto} />
        <Voce k={t('Pacchetti')} v={f.packets?.toLocaleString(localeCorrente())} />
        <Voce k={t('Byte inviati')} v={f.bytes_sent?.toLocaleString(localeCorrente())} />
        <Voce k={t('Byte ricevuti')} v={f.bytes_received?.toLocaleString(localeCorrente())} />
        <Voce k={t('Categoria')} v={f.category} />
        <Voce k={t('ID sessione')} v={f.session_id} />
        <Voce k={t('Regola')} v={f.rule} />
      </dl>
      {spiega && (
        <p className="field__hint" style={{ marginTop: 8 }}>
          <strong>{f.end_reason}</strong> — {spiega}
        </p>
      )}
      <details style={{ marginTop: 6 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
          {t('Cosa significano gli stati di sessione')}
        </summary>
        <dl style={{ margin: '6px 0 0', fontSize: 12 }}>
          {Object.entries(STATO_SPIEGA).map(([k, v]) => (
            <div key={k} style={{ marginBottom: 3 }}>
              <dt style={{ display: 'inline', fontWeight: 600 }}>{k}</dt>
              <dd style={{ display: 'inline', margin: '0 0 0 6px' }}>{v()}</dd>
            </div>
          ))}
        </dl>
      </details>
    </div>
  )
}

/**
 * La tabella dei flussi del firewall con la colonna **Stato** (motivo di fine
 * sessione) e la **riga espandibile stile Palo Alto**: il dettaglio (zone,
 * pacchetti, byte per verso, categoria, id sessione) con la spiegazione dello
 * stato e un glossario a scomparsa. Condivisa fra la scheda del CI e il Log Query.
 */
export function FirewallFlowsTable({ flows }: { flows: FirewallFlow[] }) {
  return (
    <DataTable
      rows={flows}
      initialSort={{ key: 'time', dir: 'desc' }}
      rowKey={(f, i) => `${f.time}-${f.session_id ?? ''}-${i}`}
      expand={(f) => <Dettaglio f={f} />}
      columns={[
        { key: 'time', header: t('Ora'), mono: true, value: (f) => f.time ?? '' },
        {
          key: 'action', header: t('Esito'), value: (f) => f.action ?? '',
          render: (f) => (
            <span className={f.blocked ? 'badge badge--warn' : 'badge badge--ok'}>
              {f.action ?? '—'}
            </span>
          ),
        },
        { key: 'end_reason', header: t('Stato'), value: (f) => f.end_reason ?? '' },
        { key: 'src', header: t('Sorgente'), mono: true, value: (f) => f.src ?? '' },
        { key: 'dst', header: t('Destinazione'), mono: true, value: (f) => f.dst ?? '' },
        { key: 'dport', header: t('Porta'), mono: true, value: (f) => f.dport ?? '' },
        { key: 'application', header: t('Applicazione'), value: (f) => f.application ?? '' },
        { key: 'rule', header: t('Regola'), value: (f) => f.rule ?? '' },
      ]}
    />
  )
}
